import type {
    InfinityClient,
    InfinitySignals,
    Participant,
    ParticipantID,
    ParticipantSignals,
    RoomID,
    RoomParticipantEvent,
} from '@pexip/infinity';
import type {Media} from '@pexip/media';
import type {Signal} from '@pexip/signal';
import type {InMeetingParticipant} from '@pexip/media-components';
import {difference} from '@pexip/utils';

import type {
    GetSignalType,
    JoinActivity,
    LeaveActivity,
    UpdateActivity,
    ParticipantActivity,
    RoomParticipantActivity,
    TransferActivity,
    TransferTarget,
} from '../types';
import {ParticipantActivities} from '../types';
import {createReversedMapObject} from './createReversedMapObject';
import {createConsumableKeyedBuffer} from './createConsumableKeyedBuffer';
import {
    createReversedMapSet,
    type ReversedMapSet,
} from './createReversedMapSet';

export type ParticipantsFilterKey =
    | 'waitingInLobby'
    | 'external'
    | 'raisedHand'
    | 'inMeeting'
    | 'breakout'
    | 'chair'
    | 'guest'
    | 'fecc'
    | 'directChat'
    | 'none';
export type ParticipantsSortKey = 'startTime' | '';
export interface ParticipantsGetOptions {
    participantID: ParticipantID;
    roomId: RoomID;
    filterBy: ParticipantsFilterKey;
    caching: boolean;
}
export type ClientAPIs = Pick<
    InfinityClient,
    | 'admit'
    | 'conferenceStatus'
    | 'getMe'
    | 'kick'
    | 'mute'
    | 'raiseHand'
    | 'roomId'
    | 'setRole'
    | 'spotlight'
>;
export type GetMedia = () =>
    | Pick<Media, 'muteAudio' | 'muteVideo' | 'audioMuted' | 'videoMuted'>
    | undefined;
export type InMeetingParticipantSignals = Pick<
    ParticipantSignals,
    | 'onParticipants'
    | 'onParticipantJoined'
    | 'onParticipantUpdated'
    | 'onParticipantLeft'
> &
    Pick<InfinitySignals, 'onBreakoutEnd'>;

export type RoomParticipantMap = Map<RoomID, Map<ParticipantID, Participant>>;

type CacheKey<DataType> = string & {__dataType: DataType};
type GroupCacheKey = CacheKey<'group'>;
type BreakoutCacheKey = CacheKey<'breakout'>;

// Ensure we have exactly the same empty participant list, and make sure it
// cannot be altered.
const EMPTY_SET = new Set<ParticipantID>();

export const joinActivity = (pid: ParticipantID): JoinActivity => ({
    activity: ParticipantActivities.Join,
    participantId: pid,
});
export const leaveActivity = (pid: ParticipantID): LeaveActivity => ({
    activity: ParticipantActivities.Leave,
    participantId: pid,
});
export const updateActivity = (pid: ParticipantID): UpdateActivity => ({
    activity: ParticipantActivities.Update,
    participantId: pid,
});
export const transferActivity = (
    pid: ParticipantID,
    name: string,
    target: TransferTarget,
): TransferActivity => ({
    activity: ParticipantActivities.Transfer,
    participantId: pid,
    destinationType: target,
    destination: name,
});

const toGroupCacheKey = (
    group: ParticipantsFilterKey,
    roomId: RoomID,
    participantID?: ParticipantID,
) =>
    ['group', roomId, group, participantID]
        .flatMap(key => (key ? [JSON.stringify(key)] : []))
        .join(':') as GroupCacheKey;
const toBreakoutCacheKey = (
    group: ParticipantsFilterKey,
    roomId: RoomID,
    participantID?: ParticipantID,
) =>
    ['breakout', roomId, group, participantID]
        .flatMap(key => (key ? [JSON.stringify(key)] : []))
        .join(':') as BreakoutCacheKey;

export const isWaitingToBeAdmitted = (participant: InMeetingParticipant) => {
    return participant.isWaiting || participant.serviceType === 'ivr';
};

export const isAPIHost = (participant: Participant) => {
    return participant.role === 'chair' && !participant.hasMedia;
};

export const getDisplayName = (participant: Participant) =>
    participant.overlayText || participant.displayName;

const assignGroups = (
    participant: InMeetingParticipant,
): ParticipantsFilterKey[] => {
    const keys: ParticipantsFilterKey[] = ['none'];
    // No other assignments if the participant is transferring
    if (participant.isTransferring) {
        return keys;
    }
    if (participant.supportsDirectChat) {
        keys.push('directChat');
    }
    if (isWaitingToBeAdmitted(participant)) {
        keys.push('waitingInLobby');
    } else if (participant.isExternal) {
        keys.push('external');
    } else {
        keys.push('inMeeting');
    }
    if (participant.raisedHand) {
        keys.push('raisedHand');
    }
    if (!isAPIHost(participant)) {
        keys.push('breakout');
    }
    if (participant.isHost) {
        keys.push('chair');
    } else {
        keys.push('guest');
    }
    if (participant.canFecc) {
        keys.push('fecc');
    }
    return keys;
};

/**
 * Diff two filter sets and return the difference
 */
const diffGroups = (
    oldGroups: Set<ParticipantsFilterKey>,
    newGroups: Set<ParticipantsFilterKey>,
) => {
    const diffNew = difference(newGroups, oldGroups);
    const diffOld = difference(oldGroups, newGroups);
    return {diffNew, diffOld};
};

const createInMeetingParticipant = (
    participant: Participant,
    clientAPIs: ClientAPIs,
    getMedia: GetMedia,
): InMeetingParticipant => {
    const isSelf = clientAPIs.getMe()?.uuid === participant.uuid;
    const muteAudio = isSelf
        ? () => {
              if (getMedia()?.audioMuted !== undefined) {
                  getMedia()?.muteAudio(!getMedia()?.audioMuted);
              }
          }
        : () => {
              clientAPIs.mute({
                  participantUuid: participant.uuid,
                  mute: !participant.isMuted,
              });
          };
    const muteVideo = () => {
        if (getMedia()?.videoMuted !== undefined) {
            getMedia()?.muteVideo(!getMedia()?.videoMuted);
        }
    };
    const kick = () => {
        clientAPIs.kick({
            participantUuid: participant.uuid,
        });
    };
    const admit = () => {
        clientAPIs.admit({
            participantUuid: participant.uuid,
        });
        // if role is 'chair' | 'unknown', set role to 'guest' and bypass host PIN
        if (participant.role !== 'guest') {
            clientAPIs.setRole({
                role: 'guest',
                participantUuid: participant.uuid,
            });
        }
    };
    const spotlight = () => {
        clientAPIs.spotlight({
            enable: !participant.isSpotlight,
            participantUuid: participant.uuid,
        });
    };
    const setRole = () => {
        clientAPIs.setRole({
            role: participant.isHost ? 'guest' : 'chair',
            participantUuid: participant.uuid,
        });
    };
    const lowerHand = () => {
        clientAPIs.raiseHand({
            raise: false,
            participantUuid: participant.uuid,
        });
    };
    return Object.defineProperties(participant, {
        displayName: {
            value: getDisplayName(participant),
            configurable: true,
        },
        mute: {value: muteAudio, configurable: true},
        muteVideo: {value: muteVideo, configurable: true},
        kick: {value: kick, configurable: true},
        admit: {value: admit, configurable: true},
        spotlight: {value: spotlight, configurable: true},
        setRole: {value: setRole, configurable: true},
        lowerHand: {value: lowerHand, configurable: true},
    });
};

export const createInMeetingParticipants = (
    clientApis: ClientAPIs,
    getMedia: GetMedia,
    participantsSignals: InMeetingParticipantSignals,
    activitySignal?: Signal<RoomParticipantActivity, RoomParticipantActivity[]>,
    previous?: {
        participants?: RoomParticipantMap;
        activities?: [RoomID, Set<ParticipantActivity>][];
    },
) => {
    // -----------------------------------------------------------------------
    // Internal States
    // -----------------------------------------------------------------------
    const currentParticipants = createReversedMapObject<
        RoomID,
        ParticipantID,
        InMeetingParticipant
    >();
    const oldParticipants = createReversedMapObject<
        RoomID,
        ParticipantID,
        Participant
    >(previous?.participants);
    // Buffer for participant activities
    const activityBuffer = createConsumableKeyedBuffer<
        RoomID,
        ParticipantActivity
    >(previous?.activities);
    // Participant Groups
    const groups = new Map<
        RoomID,
        ReversedMapSet<ParticipantsFilterKey, ParticipantID>
    >();
    // Caches
    const cache = new Map<RoomID, Map<GroupCacheKey, Set<ParticipantID>>>();
    const breakoutCache = new Map<
        RoomID,
        Map<BreakoutCacheKey, Set<ParticipantID>>
    >();

    // -----------------------------------------------------------------------
    // Internal Functions
    // -----------------------------------------------------------------------
    const addGroup = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        let room = groups.get(roomId);
        if (!room) {
            room = createReversedMapSet();
            groups.set(roomId, room);
        }
        if (room.has(groupKey, participant.uuid)) {
            return;
        }
        room.add(groupKey, participant.uuid);
        clearParticipantCache(participant, roomId, groupKey);
        clearGroupCache(roomId, groupKey);
    };

    const removeGroup = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        const room = groups.get(roomId);
        if (!room) {
            return false;
        }
        if (room.has(groupKey, participant.uuid)) {
            clearParticipantCache(participant, roomId, groupKey);
            clearGroupCache(roomId, groupKey);
            return room.remove(groupKey, participant.uuid);
        }
        return false;
    };

    const pushActivity = (roomId: RoomID, activity: ParticipantActivity) => {
        activityBuffer.push(roomId, activity);
        activitySignal?.emit({roomId, activity});
    };

    const setCurrentParticipant = (
        roomId: RoomID,
        participant: Participant,
    ) => {
        currentParticipants.set(roomId, participant);
        updateParticipantGroups(roomId, participant);
    };

    const getCache = (roomId: RoomID) => {
        let roomCache = cache.get(roomId);
        if (!roomCache) {
            roomCache = new Map();
            cache.set(roomId, roomCache);
        }
        return roomCache;
    };

    const clearGroupCache = (
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        cache.get(roomId)?.delete(toGroupCacheKey(groupKey, roomId));
        breakoutCache.get(roomId)?.delete(toBreakoutCacheKey(groupKey, roomId));
    };

    const clearParticipantCache = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        cache
            .get(roomId)
            ?.delete(toGroupCacheKey(groupKey, roomId, participant.uuid));
        // `inMeeting` participants are sorted based on the role, so when the role
        // changed, the cache needs to be invalidated
        if (groupKey === 'chair' || groupKey === 'guest') {
            const relatedGroups = groups
                .get(roomId)
                ?.getReversed(participant.uuid);
            if (relatedGroups?.has('inMeeting')) {
                cache.get(roomId)?.delete(toGroupCacheKey('inMeeting', roomId));
                breakoutCache
                    .get(roomId)
                    ?.delete(toBreakoutCacheKey('inMeeting', roomId));
            }
        }
        clearBreakoutParticipantCache(participant, roomId, groupKey);
    };

    const clearRoomCache = (roomId: RoomID) => {
        cache.get(roomId)?.clear();
        breakoutCache.get(roomId)?.clear();
    };

    const clearBreakoutParticipantCache = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        // Group breakout changes should invalidate the breakout cache for all related
        if (groupKey === 'breakout') {
            const relatedGroups = groups
                .get(roomId)
                ?.getReversed(participant.uuid);
            if (relatedGroups) {
                for (const group of relatedGroups) {
                    breakoutCache
                        .get(roomId)
                        ?.delete(toBreakoutCacheKey(group, roomId));
                    breakoutCache
                        .get(roomId)
                        ?.delete(
                            toBreakoutCacheKey(
                                groupKey,
                                roomId,
                                participant.uuid,
                            ),
                        );
                }
            }
        }
        breakoutCache
            .get(roomId)
            ?.delete(toBreakoutCacheKey(groupKey, roomId, participant.uuid));
    };

    const updateParticipantGroups = (
        roomId: RoomID,
        participant: InMeetingParticipant,
    ) => {
        const {diffNew, diffOld} = diffGroups(
            groups.get(roomId)?.getReversed(participant.uuid) ?? new Set(),
            new Set(assignGroups(participant)),
        );
        for (const group of diffOld) {
            removeGroup(participant, roomId, group);
        }
        for (const group of diffNew) {
            addGroup(participant, roomId, group);
        }
    };

    const handleParticipantSync = ({
        id,
        participants,
    }: GetSignalType<InMeetingParticipantSignals['onParticipants']>) => {
        groups.delete(id);
        const pidsAfterSync: Set<ParticipantID> = new Set();
        const pidsBeforeSync = new Set(currentParticipants.keys(id));
        // Update the current participants
        for (const participant of participants) {
            const newParticipant = createInMeetingParticipant(
                participant,
                clientApis,
                getMedia,
            );
            pidsAfterSync.add(participant.uuid);
            setCurrentParticipant(id, newParticipant);

            const updated = pidsBeforeSync.has(participant.uuid);
            if (updated) {
                pushActivity(id, updateActivity(participant.uuid));
            }
        }
        // Update disconnected participants
        const disconnectedPids = difference(pidsBeforeSync, pidsAfterSync);
        const joinedPids = difference(pidsAfterSync, pidsBeforeSync);
        for (const pid of disconnectedPids) {
            const participant = currentParticipants.get(id, pid);
            if (!participant) {
                continue;
            }
            handleParticipantLeft({id, participant});
        }
        for (const pid of joinedPids) {
            pushActivity(id, joinActivity(pid));
        }
        clearRoomCache(id);
    };

    const handleParticipantJoined = ({
        id,
        participant,
    }: RoomParticipantEvent) => {
        const newParticipant = createInMeetingParticipant(
            participant,
            clientApis,
            getMedia,
        );
        const hasParticipant = currentParticipants.has(id, participant.uuid);
        setCurrentParticipant(id, newParticipant);
        // Ensure we don't push a duplicated join activity
        // See https://github.com/pexip/mcu/issues/44248
        if (!hasParticipant) {
            pushActivity(id, joinActivity(participant.uuid));
        }
    };

    const handleParticipantUpdate = ({
        id,
        participant,
    }: RoomParticipantEvent) => {
        const newParticipant = createInMeetingParticipant(
            participant,
            clientApis,
            getMedia,
        );
        setCurrentParticipant(id, newParticipant);
        pushActivity(id, updateActivity(participant.uuid));
    };

    const handleParticipantLeft = ({id, participant}: RoomParticipantEvent) => {
        const deleted = currentParticipants.remove(id, participant.uuid);
        if (!deleted) {
            return;
        }
        const groupSet = groups.get(id)?.getReversed(participant.uuid);
        if (groupSet?.size) {
            for (const group of groupSet) {
                removeGroup(participant, id, group);
            }
        }
        oldParticipants.set(id, participant);
        pushActivity(id, leaveActivity(participant.uuid));
    };

    const handleBreakoutEnd = ({
        breakout_uuid,
    }: GetSignalType<InMeetingParticipantSignals['onBreakoutEnd']>) => {
        const current = currentParticipants.values(breakout_uuid);
        if (!current) {
            return;
        }
        groups.delete(breakout_uuid);
        cache.delete(breakout_uuid);
        breakoutCache.delete(breakout_uuid);
        for (const participant of current) {
            oldParticipants.set(breakout_uuid, participant);
        }
        currentParticipants.removeByPrimary(breakout_uuid);
        activityBuffer.remove(breakout_uuid);
    };

    const unsubscribes = [
        participantsSignals.onParticipants.add(handleParticipantSync),
        participantsSignals.onParticipantJoined.add(events => {
            for (const event of events) {
                handleParticipantJoined(event);
            }
        }),
        participantsSignals.onParticipantLeft.add(events => {
            for (const event of events) {
                handleParticipantLeft(event);
            }
        }),
        participantsSignals.onParticipantUpdated.add(events => {
            for (const event of events) {
                handleParticipantUpdate(event);
            }
        }),
        participantsSignals.onBreakoutEnd.add(handleBreakoutEnd),
    ];

    const sortParticipants =
        (sort: (a: Participant, b: Participant) => number) =>
        (roomId: RoomID) =>
        (a: ParticipantID, b: ParticipantID) => {
            const participantA = currentParticipants.get(roomId, a);
            const participantB = currentParticipants.get(roomId, b);
            // No comparison of non-existing participants
            if (participantA === undefined || participantB === undefined) {
                return 0;
            }
            return sort(participantA, participantB);
        };
    const sortByHandRaisedTime = sortParticipants((a, b) => {
        return a.handRaisedTime - b.handRaisedTime;
    });
    const sortByStartTime = sortParticipants((a, b) => {
        return a.startTime - b.startTime;
    });

    const findParticipant = (
        roomId: RoomID,
        filterBy: ParticipantsFilterKey,
        participantID: ParticipantID,
    ) => {
        const exists = groups.get(roomId)?.has(filterBy, participantID);
        if (exists) {
            return new Set([participantID]);
        }
        return EMPTY_SET;
    };

    const findParticipants = (
        roomId: RoomID,
        filterBy: ParticipantsFilterKey,
        participantID: ParticipantID | undefined,
    ) => {
        if (participantID !== undefined) {
            return findParticipant(roomId, filterBy, participantID);
        }
        const roomGroups = groups.get(roomId)?.get(filterBy);
        if (roomGroups) {
            switch (roomGroups.size) {
                case 0:
                    return EMPTY_SET;
                case 1: {
                    return new Set(roomGroups);
                }
                default: {
                    // Sort the result
                    switch (filterBy) {
                        case 'raisedHand': {
                            const sortBy = sortByHandRaisedTime(roomId);
                            return new Set(Array.from(roomGroups).sort(sortBy));
                        }
                        case 'inMeeting': {
                            const hosts: ParticipantID[] = [];
                            const guests: ParticipantID[] = [];
                            const sortBy = sortByStartTime(roomId);
                            for (const pid of roomGroups) {
                                const participant = currentParticipants.get(
                                    roomId,
                                    pid,
                                );
                                if (!participant) {
                                    continue;
                                }
                                if (participant.isHost) {
                                    hosts.push(pid);
                                } else {
                                    guests.push(pid);
                                }
                            }
                            return new Set([
                                ...hosts.sort(sortBy),
                                ...guests.sort(sortBy),
                            ]);
                        }
                        default: {
                            const sortBy = sortByStartTime(roomId);
                            return new Set(Array.from(roomGroups).sort(sortBy));
                        }
                    }
                }
            }
        }
        return EMPTY_SET;
    };

    return Object.freeze({
        /**
         * Return a list of participants based on the criteria from `filterBy`.
         */
        get({
            roomId = clientApis.roomId,
            participantID,
            filterBy = 'none',
            caching = true,
        }: Partial<ParticipantsGetOptions> = {}) {
            if (caching) {
                const key = toGroupCacheKey(filterBy, roomId, participantID);
                const found = getCache(roomId).get(key);
                if (found) {
                    return found;
                }
            }
            const result = findParticipants(roomId, filterBy, participantID);
            if (caching) {
                getCache(roomId).set(
                    toGroupCacheKey(filterBy, roomId, participantID),
                    result,
                );
            }
            return result;
        },
        /**
         * Return a list of participants who are NOT transferring and NOT a
         * host without media based on the criteria from `filterBy`.
         */
        getBreakout({
            roomId = clientApis.roomId,
            participantID,
            filterBy = 'none',
            caching = true,
        }: Partial<ParticipantsGetOptions> = {}) {
            const key = toBreakoutCacheKey(filterBy, roomId, participantID);
            const breakoutParticipants = caching
                ? breakoutCache.get(roomId)?.get(key)
                : undefined;
            if (breakoutParticipants) {
                return breakoutParticipants;
            }
            const pids = this.get({
                roomId,
                participantID,
                filterBy,
                caching,
            });
            const breakoutPids = new Set<ParticipantID>();
            for (const pid of pids) {
                const participant = this.getCurrent(pid, roomId);
                if (!participant) {
                    continue;
                }
                if (!participant.isTransferring && !isAPIHost(participant)) {
                    breakoutPids.add(pid);
                }
            }
            if (caching) {
                let roomCache = breakoutCache.get(roomId);
                if (!roomCache) {
                    roomCache = new Map();
                    breakoutCache.set(roomId, roomCache);
                }
                roomCache.set(key, breakoutPids);
            }
            return breakoutPids;
        },
        getGroups(participantID: ParticipantID, roomId: RoomID) {
            return groups.get(roomId)?.getReversed(participantID);
        },
        getCurrent(participantID: ParticipantID, roomId: RoomID) {
            return currentParticipants.get(roomId, participantID);
        },
        getCurrentRoom(participantID: ParticipantID) {
            return currentParticipants.getReversed(participantID);
        },
        getDisconnected(participantID: ParticipantID, roomId?: RoomID) {
            if (roomId) {
                return oldParticipants.get(roomId, participantID);
            }
            const room = oldParticipants.getReversed(participantID);
            if (!room) {
                return;
            }
            return oldParticipants.get(room, participantID);
        },
        isDisconnected(participantID: ParticipantID, roomId?: RoomID) {
            if (roomId) {
                return Boolean(oldParticipants.has(roomId, participantID));
            }
            return oldParticipants.hasReversed(participantID);
        },
        consumeActivities(roomId: RoomID) {
            return activityBuffer.consume(roomId);
        },
        hasActivities(roomId: RoomID) {
            return activityBuffer.has(roomId);
        },
        dump() {
            const participants: RoomParticipantMap = new Map();
            oldParticipants.copyTo(participants);
            currentParticipants.copyTo(participants);
            return {
                participants,
                activities: activityBuffer.dump(),
            };
        },
        transfer(name: string, target: TransferTarget) {
            const selfUUID = clientApis.getMe()?.uuid;
            if (selfUUID) {
                pushActivity(
                    clientApis.roomId,
                    transferActivity(selfUUID, name, target),
                );
            }
            return this.dump();
        },
        release() {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
            unsubscribes.length = 0;
            currentParticipants.clear();
            oldParticipants.clear();
            groups.clear();
            cache.clear();
            breakoutCache.clear();
            activityBuffer.clear();
        },
    });
};
